import { useMemo, useState, useEffect } from 'react';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import { Button, Heading, Text } from '@simplywallst/ui-core';
import type { AnalystsMap } from '../EarningsAndRevenueGrowth';
import { useTracking } from 'react-tracking';
import {
  COMPANY_EARNINGS_GROWTH_ESTIMATES,
  COMPANY_EPS_ESTIMATES,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
/** Raw Data Containers */
import {
  LegacyAnchor,
  FUTURE_EARNINGS,
  FUTURE_GROWTH,
  FUTURE_EARNINGS_PER_SHARE,
  FUTURE_RETURN_EQUITY,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
import loadable from '@loadable/component';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import EarningsAndRevenueGrowthTable from '../EarningsAndRevenueGrowth/EarningsAndRevenueGrowthTable';
import { EarningsAndRevenueGrowthWrapper } from '../EarningsAndRevenueGrowth/styled';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useVisibleSectionsIsPristine } from '../../../VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import type { StatementName } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import { useCompany, useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { getStatementChecks } from '@/pages/CompanyReport/components/Introduction/utils';
import Introduction from '@/pages/CompanyReport/components/Introduction';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { formatterPercentage } from '@/utilities/format';
import { Mark } from './styled';
import { calcFormattedLastUpdatedTimestamp } from './utils';
import {
  useFutureTranslation,
  FutureTranslation,
} from '../../hooks/useFutureTranslation';
import { useDateFormatter } from '@/hooks/useDateFormatter';

const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const ROEGauge = loadable(
  () => import('@/pages/CompanyReport/components/Future/components/ROEGauge'),
  {
    ssr: false,
    resolveComponent: (c) => c.ROEGauge,
  }
);
const EarningsPerShareGrowthRawDataContainer = loadable(
  () => import('@/pages/CompanyReport/components/Future'),
  {
    ssr: false,
    resolveComponent: (component) =>
      component.EarningsPerShareGrowthRawDataContainer,
  }
);
const EarningsPerShareGrowth = loadable(
  () => import('../EarningsPerShareGrowth'),
  { ssr: false }
);
const FutureGrowthRatesRawData = loadable(
  () => import('@/pages/CompanyReport/components/Future'),
  {
    ssr: false,
    resolveComponent: (component) => component.FutureGrowthRatesRawData,
  }
);
const FutureGrowth = loadable(
  () => import('@/pages/CompanyReport/components/Future'),
  {
    ssr: false,
    resolveComponent: (component) => component.FutureGrowth,
  }
);

const EarningsAndRevenueGrowth = loadable(
  () => import('../EarningsAndRevenueGrowth'),
  { ssr: false }
);

const CriteriaChecks = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/CriteriaChecks'
    )
);
const KeyInformation = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/KeyInformation'
    )
);
const RecentSectionUpdates = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Introduction/components/RecentSectionUpdates'
    )
);

const NextStep = loadable(
  () => import('@/pages/CompanyReport/components/SimilarCompanies/NextStep')
);

export interface FutureProps {
  analystCount: number;
  companyId: string;
  companyName: string;
  currencyISO: string;
  currencyUnit: number;
  earningsGrowthAnnual: number | null;
  earningsGrowthAnnualFormatted: string;
  futureROE3Y: number;
  industryName: string;
  industryROE3Y: number;
  isFund: boolean;
  marketName: string;
  netIncomeGrowthAnnualIndustry: number;
  netIncomeGrowthAnnualIndustryFormatted: string;
  netIncomeGrowthAnnualMarket: number;
  netIncomeGrowthAnnualMarketFormatted: string;
  revenueGrowthAnnual: number | null;
  revenueGrowthAnnualFormatted: string;
  revenueGrowthAnnualIndustry: number;
  revenueGrowthAnnualIndustryFormatted: string;
  revenueGrowthAnnualMarket: number;
  revenueGrowthAnnualMarketFormatted: string;
  sectionNumber: number;
  /**
   * Earnings and Revenue Growth Forecasts
   */
  cashflow: number[][];
  cashflowMax: number;
  cashflowMin: number;
  cashflowAnalystsMap: AnalystsMap;
  dayZero: number;
  earnings: number[][];
  earningsMax: number;
  earningsMin: number;
  earningsAnalystsMap: AnalystsMap;
  freeCashflow: number[][];
  freeCashflowMax: number;
  freeCashflowMin: number;
  freeCashflowAnalystsMap: AnalystsMap;
  revenue: number[][];
  revenueMax: number;
  revenueMin: number;
  revenueAnalystsMap: AnalystsMap;
  /**
   * Earnings per Share Growth Forecasts
   */
  EPS: number[][];
  EPSDayZero: number;
  EPSMax: number;
  EPSMin: number;
  EPSAnalysts: {
    [date: number]: number;
  };
  EPSAnalystConfirmation: {
    [date: number]: number;
  };
  analystsEPSRangeMax: number;
  analystsEPSRangeMin: number;
  analystsPastEPSRangeLow: number[][];
  analystsPastEPSRangeHigh: number[][];
  analystsFutureEPSRangeLow: number[][];
  analystsFutureEPSRangeHigh: number[][];
}

export const Future = ({
  analystCount,
  companyId,
  companyName,
  currencyISO,
  currencyUnit,
  earningsGrowthAnnual,
  futureROE3Y,
  industryName,
  industryROE3Y,
  isFund,
  marketName,
  netIncomeGrowthAnnualIndustry,
  netIncomeGrowthAnnualMarket,
  revenueGrowthAnnual,
  revenueGrowthAnnualIndustry,
  revenueGrowthAnnualMarket,
  sectionNumber,
  earningsGrowthAnnualFormatted,
  netIncomeGrowthAnnualIndustryFormatted,
  netIncomeGrowthAnnualMarketFormatted,
  revenueGrowthAnnualFormatted,
  revenueGrowthAnnualIndustryFormatted,
  revenueGrowthAnnualMarketFormatted,
  /**
   * Earnings and Revenue Growth Forecasts
   */
  cashflow,
  cashflowMax,
  cashflowMin,
  cashflowAnalystsMap,
  dayZero,
  earnings,
  earningsMax,
  earningsMin,
  earningsAnalystsMap,
  freeCashflow,
  freeCashflowMax,
  freeCashflowMin,
  freeCashflowAnalystsMap,
  revenue,
  revenueMax,
  revenueMin,
  revenueAnalystsMap,
  /**
   * Earnings per Share Growth Forecasts
   */
  EPS,
  EPSAnalysts,
  EPSAnalystConfirmation,
  EPSDayZero,
  EPSMax,
  EPSMin,
  analystsEPSRangeMax,
  analystsEPSRangeMin,
  analystsPastEPSRangeLow,
  analystsPastEPSRangeHigh,
  analystsFutureEPSRangeLow,
  analystsFutureEPSRangeHigh,
}: FutureProps) => {
  const isPristine = useVisibleSectionsIsPristine();
  const [showReport, setShowReport] = useState(true);
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'Future',
    section: 'Future',
  });
  const trackingButtonData = {
    action: 'click',
    modifier: 'show_Meaningless_data',
  };

  const canonicalUrl = useCompanyCanonicalUrl();

  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });
  const term = useGlossaryTerms();

  const { data } = useCompany(canonicalUrl);

  const {
    last_revenue_estimate_confirmed: revenueTimestamp,
    last_net_income_estimate_confirmed: incomeTimestamp,
    earnings_per_share_growth_annual: epsGrowthAnnual,
  } = data?.data.analysis?.data.extended.data.analysis.future || {};

  const formattedLastUpdatedTimestamp = useMemo(
    () => calcFormattedLastUpdatedTimestamp(revenueTimestamp, incomeTimestamp),
    [incomeTimestamp, revenueTimestamp]
  );

  const {
    IsExpectedProfitGrowthAboveRiskFreeRate:
      isExpectedProfitGrowthAboveRiskFreeRate,
    IsExpectedAnnualProfitGrowthAboveMarket:
      isExpectedAnnualProfitGrowthAboveMarket,
    IsExpectedAnnualProfitGrowthHigh: isExpectedAnnualProfitGrowthHigh,
    IsExpectedRevenueGrowthAboveMarket: isExpectedRevenueGrowthAboveMarket,
    IsExpectedRevenueGrowthHigh: isExpectedRevenueGrowthHigh,
    IsReturnOnEquityForecastAboveBenchmark:
      isReturnOnEquityForecastAboveBenchmark,
    IsFutureAnalysisMeaningless: isFutureAnalysisMeaningless,
  } = statements || {};

  const futureStatements: StatementName[] = [
    'IsExpectedProfitGrowthAboveRiskFreeRate',
    'IsExpectedAnnualProfitGrowthAboveMarket',
    'IsExpectedAnnualProfitGrowthHigh',
    'IsExpectedRevenueGrowthAboveMarket',
    'IsExpectedRevenueGrowthHigh',
    'IsReturnOnEquityForecastAboveBenchmark',
  ];
  const statementChecks = getStatementChecks(futureStatements, statements);

  const isDataMeaningless = isFutureAnalysisMeaningless?.value === true;

  useEffect(() => {
    setShowReport(isDataMeaningless === false);
  }, [isDataMeaningless]);

  const {
    growthInEarningsDownloadUrl,
    growthExpectationsDownloadUrl,
    epsDownloadUrl,
    futureROEDownloadUrl,
  } = useMemo(() => {
    return {
      growthInEarningsDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FutureGrowth
      ),
      growthExpectationsDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.EarningsAndRevenueGrowth
      ),
      epsDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.EarningsPerShareGrowth
      ),
      futureROEDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FutureROE
      ),
    };
  }, [companyId]);

  const markValue = useMemo(() => {
    if (analystCount <= 0) return 'None';
    if (analystCount > 0 && analystCount <= 5) return 'Low';
    return 'Good';
  }, [analystCount]);

  const t = useFutureTranslation();
  const { format } = useDateFormatter();

  return (
    <Track>
      <ReportSection sectionNumber={sectionNumber} data-cy-id="future-section">
        <CompanySectionTitle title={t('heading')} />
        <Introduction
          scoreName="Future"
          criteriaChecks={
            <CriteriaChecks scoreName="Future" checks={statementChecks} />
          }
          keyInformation={
            <KeyInformation
              heading={
                <Heading as="h3" typography="small">
                  {t('introduction.keyInfo.heading')}
                </Heading>
              }
              keyMetrics={[
                {
                  title: t('introduction.keyMetric.earnings'),
                  value: earningsGrowthAnnualFormatted,
                  tooltip: term('earnings growth rate'),
                },
                {
                  title: t('introduction.keyMetric.epsGrowth'),
                  value: formatterPercentage(epsGrowthAnnual),
                  tooltip: term('earnings per share growth rate'),
                },
              ]}
              metrics={[
                {
                  title: t('introduction.metric.earnings', {
                    industry: industryName,
                  }),
                  value: netIncomeGrowthAnnualIndustryFormatted,
                  tooltip: `${industryName} ${term('industry growth rate')}`,
                },
                {
                  title: t('introduction.metric.revenue'),
                  value: revenueGrowthAnnualFormatted,
                  tooltip: term('revenue growth rate'),
                },
                {
                  title: t('introduction.metric.future'),
                  value: formatterPercentage(futureROE3Y),
                  tooltip: term('future return on equity'),
                },
                {
                  title: t('introduction.metric.analyst'),
                  value: (
                    <Text typography="tiny">
                      <Mark value={markValue}>{markValue}</Mark>
                    </Text>
                  ),
                  tooltip: term('future growth analyst Coverage'),
                },
                {
                  title: t('introduction.metric.lastUpdated'),
                  value: formattedLastUpdatedTimestamp,
                  tooltip: term('future growth last updated'),
                },
              ]}
            />
          }
          recentSectionUpdates={
            <RecentSectionUpdates
              lookup={canonicalUrl}
              title={t('introduction.recentUpdates.heading')}
              filterByEventCategory="future"
            />
          }
        />

        <BrazeContentCardWrapper>
          <BrazeContentCard
            feedType="company-overview.future"
            anchorTarget="_blank"
          />
        </BrazeContentCardWrapper>
        {isDataMeaningless && (
          <ReportSubSection
            body={
              <>
                <FutureTranslation
                  i18nKey="insufficientData.description"
                  values={{ company: companyName }}
                >
                  <p>
                    In this section we usually present revenue and earnings
                    growth projections based on the consensus estimates of
                    professional analysts to help investors understand the
                    company’s ability to generate profit. But as {companyName}{' '}
                    has not provided enough past data and has no analyst
                    forecast, its future earnings cannot be reliably calculated
                    by extrapolating past data or using analyst predictions.
                  </p>
                  <p>
                    This is quite a rare situation as 97% of companies covered
                    by SimplyWall St do have past financial data.
                  </p>
                </FutureTranslation>
                {showReport === false && (
                  <Button
                    styleType="louderLarge"
                    onClick={() => {
                      trackEvent(trackingButtonData);
                      setShowReport(!showReport);
                    }}
                  >
                    {t('insufficientData.showSection')}
                  </Button>
                )}
              </>
            }
          />
        )}

        {showReport && (
          <>
            <PageAnchor type="sub-section-title" id={FUTURE_EARNINGS} />
            <PageAnchor
              type="sub-section-title"
              id={LegacyAnchor.INCOME_ESTIMATE}
            />
            <ReportSubSection
              titleElement={subSectionTitleElement}
              title={t('revenue.heading')}
              trackingProps={{ subject: 'chart' }}
              bodyRender={() => (
                <ChartWrapper
                  id="future-earnings-revenue-growth-chart"
                  chart={
                    <EarningsAndRevenueGrowthWrapper>
                      <EarningsAndRevenueGrowth
                        cashflow={cashflow}
                        cashflowMax={cashflowMax}
                        cashflowMin={cashflowMin}
                        cashflowAnalystsMap={cashflowAnalystsMap}
                        currencyISO={currencyISO}
                        currencyUnit={currencyUnit}
                        earnings={earnings}
                        earningsMax={earningsMax}
                        earningsMin={earningsMin}
                        earningsAnalystsMap={earningsAnalystsMap}
                        revenue={revenue}
                        revenueMax={revenueMax}
                        revenueMin={revenueMin}
                        revenueAnalystsMap={revenueAnalystsMap}
                        dayZero={dayZero}
                        freeCashflow={freeCashflow}
                        freeCashflowMax={freeCashflowMax}
                        freeCashflowMin={freeCashflowMin}
                        freeCashflowAnalystsMap={freeCashflowAnalystsMap}
                        labels={{
                          past: t('revenue.chart.pastLabel'),
                          future: t('revenue.chart.futureLabel'),
                          notAvailable: t('revenue.chart.notAvailable'),
                          legendAnalysts: t('revenue.chart.legend.analysts'),
                          legendLastUpdated: t(
                            'revenue.chart.legend.lastUpdated'
                          ),
                          seriesCashFromOp: t(
                            'revenue.chart.series.cashFromOp'
                          ),
                          seriesFreeCashFlow: t(
                            'revenue.chart.series.freeCashFlow'
                          ),
                          seriesEarnings: t('revenue.chart.series.earnings'),
                          seriesRevenue: t('revenue.chart.series.revenue'),
                        }}
                      />
                    </EarningsAndRevenueGrowthWrapper>
                  }
                  table={
                    <EarningsAndRevenueGrowthWrapper>
                      <EarningsAndRevenueGrowthTable />
                    </EarningsAndRevenueGrowthWrapper>
                  }
                  feedbackLabel={COMPANY_EARNINGS_GROWTH_ESTIMATES}
                  exportDarkUrl={growthExpectationsDownloadUrl}
                />
              )}
            />
            <hr />
            <PageAnchor type="sub-section-title" id={FUTURE_GROWTH} />
            <ReportSubSection
              titleElement={subSectionTitleElement}
              title={t('analyst.heading')}
              trackingProps={{ subject: 'chart' }}
              bodyRender={(title) => (
                <ChartWrapper
                  id="future-analyst-future-growth-chart"
                  preRenderHeight="305px"
                  preRenderMinHeightMdDown="300px"
                  preRenderMinHeightSmDown="600px"
                  chart={
                    <FutureGrowth
                      companyName={companyName}
                      industryName={industryName}
                      marketName={marketName}
                      earningsGrowthAnnual={earningsGrowthAnnual}
                      revenueGrowthAnnual={revenueGrowthAnnual}
                      netIncomeGrowthAnnualIndustry={
                        netIncomeGrowthAnnualIndustry
                      }
                      revenueGrowthAnnualIndustry={revenueGrowthAnnualIndustry}
                      netIncomeGrowthAnnualMarket={netIncomeGrowthAnnualMarket}
                      revenueGrowthAnnualMarket={revenueGrowthAnnualMarket}
                      earningsGrowthAnnualFormatted={
                        earningsGrowthAnnualFormatted
                      }
                      netIncomeGrowthAnnualIndustryFormatted={
                        netIncomeGrowthAnnualIndustryFormatted
                      }
                      netIncomeGrowthAnnualMarketFormatted={
                        netIncomeGrowthAnnualMarketFormatted
                      }
                      revenueGrowthAnnualFormatted={
                        revenueGrowthAnnualFormatted
                      }
                      revenueGrowthAnnualIndustryFormatted={
                        revenueGrowthAnnualIndustryFormatted
                      }
                      revenueGrowthAnnualMarketFormatted={
                        revenueGrowthAnnualMarketFormatted
                      }
                      labels={{
                        company: t('analyst.chart.company'),
                        industry: t('analyst.chart.industry'),
                        market: t('analyst.chart.market'),
                        chartEarnings: t('analyst.chart.earnings'),
                        chartRevenue: t('analyst.chart.revenue'),
                      }}
                    />
                  }
                  rawDataModal={
                    <FutureGrowthRatesRawData companyId={companyId} />
                  }
                  rawDataTitle={title}
                  feedbackLabel={t('analyst.chart.feedback')}
                  exportDarkUrl={growthInEarningsDownloadUrl}
                />
              )}
              statements={
                <>
                  <Statement
                    title={isExpectedProfitGrowthAboveRiskFreeRate?.title}
                    description={
                      <Description
                        statement={isExpectedProfitGrowthAboveRiskFreeRate}
                      />
                    }
                    value={isExpectedProfitGrowthAboveRiskFreeRate?.value}
                    statementId={CompanyStatements.futureStatement1}
                  />
                  <Statement
                    title={isExpectedAnnualProfitGrowthAboveMarket?.title}
                    description={
                      <Description
                        statement={isExpectedAnnualProfitGrowthAboveMarket}
                      />
                    }
                    value={isExpectedAnnualProfitGrowthAboveMarket?.value}
                    statementId={CompanyStatements.futureStatement2}
                  />
                  <Statement
                    title={isExpectedAnnualProfitGrowthHigh?.title}
                    description={
                      <Description
                        statement={isExpectedAnnualProfitGrowthHigh}
                      />
                    }
                    value={isExpectedAnnualProfitGrowthHigh?.value}
                    statementId={CompanyStatements.futureStatement3}
                  />
                  <Statement
                    title={isExpectedRevenueGrowthAboveMarket?.title}
                    description={
                      <Description
                        statement={isExpectedRevenueGrowthAboveMarket}
                      />
                    }
                    value={isExpectedRevenueGrowthAboveMarket?.value}
                    statementId={CompanyStatements.futureStatement4}
                  />
                  <Statement
                    title={isExpectedRevenueGrowthHigh?.title}
                    description={
                      <Description statement={isExpectedRevenueGrowthHigh} />
                    }
                    value={isExpectedRevenueGrowthHigh?.value}
                    statementId={CompanyStatements.futureStatement5}
                  />
                </>
              }
            />
            <hr />
            <PageAnchor
              type="sub-section-title"
              id={FUTURE_EARNINGS_PER_SHARE}
            />
            <PageAnchor
              type="sub-section-title"
              id={LegacyAnchor.PAST_FUTURE_EARNINGS}
            />
            <ReportSubSection
              titleElement={subSectionTitleElement}
              title={t('earningsPerShare.heading')}
              trackingProps={{ subject: 'chart' }}
              bodyRender={(title) => (
                <ChartWrapper
                  id="earnings-per-share-growth"
                  preRenderHeight="500px"
                  preRenderMinHeightSmDown="442px"
                  chart={
                    <EarningsPerShareGrowth
                      EPS={EPS}
                      EPSMax={EPSMax}
                      EPSMin={EPSMin}
                      EPSAnalysts={EPSAnalysts}
                      EPSAnalystConfirmation={EPSAnalystConfirmation}
                      analystsEPSRangeMax={analystsEPSRangeMax}
                      analystsEPSRangeMin={analystsEPSRangeMin}
                      analystsPastEPSRangeLow={analystsPastEPSRangeLow}
                      analystsPastEPSRangeHigh={analystsPastEPSRangeHigh}
                      analystsFutureEPSRangeLow={analystsFutureEPSRangeLow}
                      analystsFutureEPSRangeHigh={analystsFutureEPSRangeHigh}
                      currencyISO={currencyISO}
                      dayZero={EPSDayZero}
                      labels={{
                        past: t('earningsPerShare.chart.past'),
                        future: t('earningsPerShare.chart.future'),
                        seriesEPS: t('earningsPerShare.chart.seriesEPS'),
                        seriesEPSRange: t(
                          'earningsPerShare.chart.seriesEPSRange'
                        ),
                        seriesEPSRangeAnalystCount: (count) =>
                          t(
                            'earningsPerShare.chart.seriesEPSRangeAnalystCount',
                            {
                              count,
                            }
                          ),
                        seriesEPSRangeLastConfirmed: (date) =>
                          t(
                            'earningsPerShare.chart.seriesEPSRangeLastConfirmed',
                            {
                              date: format(date, 'MMM dd yyyy'),
                            }
                          ),
                        seriesDataPointDate: (date) =>
                          format(date, 'MMM dd yyyy'),
                        notAvailable: t('earningsPerShare.chart.notAvailable'),
                      }}
                    />
                  }
                  rawDataModal={
                    <EarningsPerShareGrowthRawDataContainer
                      companyId={companyId}
                      companyShortName={companyName}
                    />
                  }
                  rawDataTitle={title}
                  feedbackLabel={COMPANY_EPS_ESTIMATES}
                  exportDarkUrl={epsDownloadUrl}
                />
              )}
            />
            <hr />
            <PageAnchor type="sub-section-title" id={FUTURE_RETURN_EQUITY} />
            <ReportSubSection
              titleElement={subSectionTitleElement}
              title={t('futureReturn.heading')}
              trackingProps={{ subject: 'chart' }}
              split={true}
              body={
                <ChartWrapper
                  id="future-return-on-equity"
                  preRenderHeight="280px"
                  preRenderMinHeightMdDown="275px"
                  chart={
                    <ROEGauge
                      companyROE={futureROE3Y}
                      companyName={companyName}
                      industryROE={industryROE3Y}
                      industryName={industryName}
                      labels={{
                        title: t('futureReturn.chart.title'),
                        company: t('futureReturn.chart.company'),
                        industry: t('futureReturn.chart.industry'),
                        notAvailable: t('futureReturn.chart.notAvailable'),
                      }}
                    />
                  }
                  feedbackLabel={t('futureReturn.chart.feedback')}
                  exportDarkUrl={futureROEDownloadUrl}
                />
              }
              statements={
                <Statement
                  title={isReturnOnEquityForecastAboveBenchmark?.title}
                  description={
                    <Description
                      statement={isReturnOnEquityForecastAboveBenchmark}
                    />
                  }
                  value={isReturnOnEquityForecastAboveBenchmark?.value}
                  statementId={CompanyStatements.futureStatement6}
                />
              }
            />
          </>
        )}
        {isFund === false && (
          <>
            <hr />
            <NextStep section="future" title={t('discover')} />
          </>
        )}
      </ReportSection>
    </Track>
  );
};
